import { useState } from 'react';
import { useParams } from 'react-router';

import { useCategories } from '@/api/category/use-categories.ts';
import { useCreateManualTransferRecord } from '@/api/transfer/use-create-manual-transfer-record.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { parsePrice } from '@/helpers/formatters.ts';

type ManualSplitFormProps = {
  transferId: string;
  onClose: () => void;
};

const ManualSplitForm = ({ transferId, onClose }: ManualSplitFormProps) => {
  const { projectId } = useParams();
  const { data: categories } = useCategories(projectId);
  const { mutateAsync: createManualRecord, isPending } = useCreateManualTransferRecord(transferId);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');

  const handleSubmit = () => {
    if (!amount || !categoryId) {
      return;
    }

    createManualRecord({
      description: description || undefined,
      amount: parsePrice(Number(amount)),
      categoryId,
    })
      .then(() => {
        onClose();
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-2 border rounded-lg p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground">
          No receipt for this transfer — split part of it manually.
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          className="flex-1"
          placeholder="Description (comma separated items)"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Input
          className="sm:w-24"
          type="number"
          step="0.01"
          placeholder="Amount"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <select
          className="flex-1 h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          <option value="">Select category</option>
          {categories?.map((category) => (
            <option key={category.uuid} value={category.uuid}>
              {category.name}
            </option>
          ))}
        </select>
        <Button size="sm" disabled={!amount || !categoryId || isPending} onClick={handleSubmit}>
          Split
        </Button>
      </div>
    </div>
  );
};

export default ManualSplitForm;
